import React from "react";
import { useNavigate } from "react-router-dom";
import AgentService from "../../services/api/agents";
import { API_RESPONSE, planAction, projectType } from "../../settings/shared/constants";
import { showNotificationGlobal } from "../common/NotificationProvider";
import TextareaInput from "./TextareaInput";
import TextRotator from './TextRotator';
import { isPocTenant } from "../../utils/premiumAccess";
import { buildPlanRequestBody, isPlanAppType, writeComposerMode } from "./planUtils";

const styles = {
    container: {
        minHeight: 'calc(100vh - 60px)',
        padding: '2rem',
        position: 'relative',
        zIndex: 1
    },
    mainTitle: { animation: 'fadeInUp 0.8s ease-out' },
    heading: {
        fontSize: '3rem',
        fontWeight: '600',
        color: '#000',
        textShadow: '0 4px 8px rgba(0,0,0,0.1)',
        lineHeight: '1.1'
    },
    gradientText: {
        fontWeight: 'bold',
        background: 'linear-gradient(to right, rgb(106, 14, 191), rgba(99, 44, 213, 0.76), rgb(211, 95, 211)) text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
    },
    generateGradientText: {
        fontWeight: 'bold',
        background: 'linear-gradient(to right, #000, #000, #673AB7) text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
    },
    stepText: { fontSize: '2rem' }
};

const LANDING_FLOW_CSS = `
  .sa-landing-modes-wrap {
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 0 0 10px;
  }
  .sa-landing-modes {
    display: inline-flex;
    align-items: stretch;
    width: fit-content;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    font-family: 'DM Sans', system-ui, sans-serif;
  }
  .sa-landing-mode {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border: none;
    background: transparent;
    border-radius: 999px;
    padding: 5px 12px;
    min-height: 28px;
    white-space: nowrap;
    cursor: pointer;
    color: #64748b;
    font-family: inherit;
    font-size: 12px;
    font-weight: 650;
    line-height: 1;
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;
  }
  .sa-landing-mode svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
  .sa-landing-mode.is-active {
    background: #fff;
    color: var(--active-color, #033b70);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    cursor: default;
  }
  .sa-landing-mode:not(.is-active):hover {
    color: var(--active-color, #033b70);
    background: rgba(255, 255, 255, 0.65);
  }
  .sa-landing-mode-pro {
    display: inline-flex;
    align-items: center;
    height: 15px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b45309;
    background: rgba(245, 158, 11, 0.16);
    border: 1px solid rgba(217, 119, 6, 0.28);
  }
`;

function IcPlus() {
    return (
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}

function IcImport() {
    return (
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 11.5V13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M8 2.5v8M5 8l3 2.5L11 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

const LandingChatView = () => {
    const navigate = useNavigate();
    const rotatingTexts = ['Agentic'];

    React.useEffect(() => {
        const id = "sa-landing-flow-styles";
        let tag = document.getElementById(id);
        if (!tag) {
            tag = document.createElement("style");
            tag.id = id;
            document.head.appendChild(tag);
        }
        tag.textContent = LANDING_FLOW_CSS;
        const legacy = document.getElementById("sa-landing-entry-styles");
        if (legacy) legacy.remove();
    }, []);

    const handleChatSubmit = async (e, submitData) => {
        e.preventDefault();
        if (!submitData) return;

        const formData = new FormData();

        const basePayload = isPlanAppType(submitData.appType)
            ? buildPlanRequestBody({ prompt: submitData.text, planAction: planAction.START })
            : { type: projectType.NEW, prompt: submitData.text, appType: submitData.appType };

        const payload = submitData.stackId
            ? { ...basePayload, stackId: submitData.stackId }
            : basePayload;

        formData.append(
          "project",
          new Blob(
            [JSON.stringify(payload)],
            { type: "application/json" }
          )
        );

        const isXray = String(submitData.appType || "").toUpperCase() === "XRAY";
        if (!isXray) {
          submitData.uploadedFiles.forEach(item => {
            formData.append("file", item.file);
          });
        }

        const response = await AgentService.createProject(formData);
        if (response?.status === API_RESPONSE.SUCCESS) {
          if (isPlanAppType(submitData.appType)) {
            writeComposerMode(response.projectDTO.id, "PLAN");
          }
          navigate(`/project/${response.projectDTO.id}`);
        } else {
          showNotificationGlobal(response.errorMessage, 'error');
        }
    };

    return (
        <div className="chat-container d-flex flex-column align-items-center justify-content-center" style={styles.container}>
            <div className="main-title text-center mb-1" style={styles.mainTitle}>
                <h1 className="mb-4" style={styles.heading}>
                    Build
                      &nbsp;<TextRotator texts={rotatingTexts} period={2000}/>
                    <span style={{ ...styles.gradientText, fontSize: '3.5rem' }}>Apps </span>in minutes
                </h1>
                <div className="steps">
                    {['Generate', 'Preview', 'Ship'].map((step, idx) => (
                        <React.Fragment key={step}>
                            {idx > 0 && <div className="arrow"><span>➜</span></div>}
                            <div className="step">
                                <h3 style={{ ...styles.generateGradientText, ...styles.stepText }}>{step}</h3>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="chat-input-section">
                <div className="sa-landing-modes-wrap">
                    <div className="sa-landing-modes" role="tablist" aria-label="Start options">
                        <button
                            type="button"
                            className="sa-landing-mode is-active"
                            role="tab"
                            aria-selected="true"
                        >
                            <IcPlus />
                            Create new
                        </button>
                        <button
                            type="button"
                            className="sa-landing-mode"
                            role="tab"
                            aria-selected="false"
                            onClick={() => navigate("/xray", { state: { openImportSources: true } })}
                        >
                            <IcImport />
                            Import existing{isPocTenant() ? <span className="sa-landing-mode-pro">Pro</span> : null}
                        </button>
                    </div>
                </div>
                <TextareaInput onSubmit={handleChatSubmit} />
            </div>
        </div>
    );
};

export default LandingChatView;
